/**
 * RadarSearch Component
 *
 * Global search modal for finding employees (⌘K / Ctrl+K).
 * Features:
 * - Keyboard shortcut activation
 * - Debounced search using existing workforce autocomplete
 * - Recent searches with employee names (persisted)
 * - Favorites with employee names
 * - Keyboard navigation
 */
import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Search, Clock, Star, X, ArrowUp, ArrowDown, CornerDownLeft } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useEmployeeAutocomplete } from '@/hooks/useWorkforce'
import { useRadarUIStore, type RecentEmployee } from '@/stores/radar-ui-store'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { EmployeeAvatar } from '@/components/vacation/EmployeeAvatar'
import { Skeleton } from '@/components/ui/skeleton'

export function RadarSearch() {
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const {
    isSearchOpen,
    closeSearch,
    recentSearches,
    addRecentSearch,
    favorites,
    toggleFavorite,
    isFavorite,
  } = useRadarUIStore()

  const [query, setQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const debouncedQuery = useDebouncedValue(query, 300)

  // Use existing workforce autocomplete hook instead of custom radar search
  const { suggestions, loading } = useEmployeeAutocomplete(debouncedQuery, {
    enabled: debouncedQuery.length >= 2,
    limit: 10
  })

  // Focus input when modal opens
  useEffect(() => {
    if (isSearchOpen && inputRef.current) {
      inputRef.current.focus()
      setQuery('')
      setSelectedIndex(0)
    }
  }, [isSearchOpen])

  // Reset selection when results change
  useEffect(() => {
    setSelectedIndex(0)
  }, [suggestions])

  // Get visible items (search results, recent, or favorites)
  type VisibleItem =
    | { type: 'result'; item: typeof suggestions[0] }
    | { type: 'recent'; item: RecentEmployee }
    | { type: 'favorite'; item: RecentEmployee }

  const getVisibleItems = useCallback((): VisibleItem[] => {
    if (debouncedQuery.length >= 2 && suggestions.length > 0) {
      return suggestions.map((item) => ({ type: 'result' as const, item }))
    }

    const items: VisibleItem[] = []

    // Add favorites first
    favorites.forEach((emp) => {
      items.push({ type: 'favorite', item: emp })
    })

    // Add recent searches (excluding favorites)
    recentSearches.forEach((emp) => {
      if (!favorites.some(f => f.employeeId === emp.employeeId)) {
        items.push({ type: 'recent', item: emp })
      }
    })

    return items.slice(0, 10)
  }, [debouncedQuery, suggestions, favorites, recentSearches])

  const visibleItems = getVisibleItems()

  // Navigate to employee
  const handleSelect = useCallback((employee: RecentEmployee) => {
    addRecentSearch(employee)
    closeSearch()
    navigate({ to: '/performance/radar/$employeeId', params: { employeeId: employee.employeeId } })
  }, [navigate, addRecentSearch, closeSearch])

  // Keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex((prev) => Math.min(prev + 1, visibleItems.length - 1))
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex((prev) => Math.max(prev - 1, 0))
        break
      case 'Enter':
        e.preventDefault()
        if (visibleItems[selectedIndex]) {
          const selected = visibleItems[selectedIndex]
          const employee: RecentEmployee = selected.type === 'result'
            ? {
                employeeId: selected.item.employeeId,
                name: selected.item.name,
                pictureUrl: selected.item.pictureUrl || null
              }
            : selected.item
          handleSelect(employee)
        }
        break
      case 'Escape':
        e.preventDefault()
        closeSearch()
        break
    }
  }, [visibleItems, selectedIndex, handleSelect, closeSearch])

  // Global keyboard shortcut
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        useRadarUIStore.getState().toggleSearch()
      }
    }

    document.addEventListener('keydown', handleGlobalKeyDown)
    return () => document.removeEventListener('keydown', handleGlobalKeyDown)
  }, [])

  if (!isSearchOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]"
      onClick={closeSearch}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Modal */}
      <div
        className="relative w-full max-w-2xl mx-4 bg-brand-dark border border-brand-gray/30 rounded-xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search input */}
        <div className="flex items-center gap-3 p-4 border-b border-brand-gray/20">
          <Search className="w-5 h-5 text-brand-gray" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Buscar funcionários..."
            className="flex-1 bg-transparent text-white placeholder-brand-gray outline-none text-lg"
          />
          <kbd className="hidden sm:flex items-center gap-1 px-2 py-1 text-xs text-brand-gray bg-brand-dark-light rounded">
            ESC
          </kbd>
          <button onClick={closeSearch} className="text-brand-gray hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-[400px] overflow-y-auto">
          {/* Loading state */}
          {loading && query.length >= 2 && (
            <div className="p-4 space-y-2">
              <Skeleton className="h-12 w-full bg-brand-gray/20" />
              <Skeleton className="h-12 w-full bg-brand-gray/20" />
              <Skeleton className="h-12 w-full bg-brand-gray/20" />
            </div>
          )}

          {/* No results */}
          {!loading && query.length >= 2 && suggestions.length === 0 && (
            <div className="p-8 text-center text-brand-gray">
              <p>Nenhum funcionário encontrado para "{query}"</p>
            </div>
          )}

          {/* Search results */}
          {!loading && query.length >= 2 && suggestions.length > 0 && (
            <div className="py-2">
              <div className="px-4 py-2 text-xs font-medium text-brand-gray uppercase">
                Resultados para "{query}"
              </div>
              {suggestions.map((result, index) => (
                <div
                  key={result.employeeId}
                  className={cn(
                    'px-4 py-2 flex items-center gap-3 cursor-pointer hover:bg-brand-dark-light',
                    selectedIndex === index && 'bg-brand-dark-light'
                  )}
                  onMouseEnter={() => setSelectedIndex(index)}
                  onClick={() => handleSelect({
                    employeeId: result.employeeId,
                    name: result.name,
                    pictureUrl: result.pictureUrl || null
                  })}
                >
                  <EmployeeAvatar
                    src={result.pictureUrl}
                    name={result.name}
                    size="sm"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-white truncate">{result.name}</div>
                    <div className="text-sm text-brand-gray truncate">{result.email}</div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleFavorite({
                        employeeId: result.employeeId,
                        name: result.name,
                        pictureUrl: result.pictureUrl || null
                      })
                    }}
                    className="text-brand-gray hover:text-yellow-400"
                  >
                    <Star
                      className={cn(
                        'w-4 h-4',
                        isFavorite(result.employeeId) && 'text-yellow-400 fill-yellow-400'
                      )}
                    />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Default state: Recent and Favorites */}
          {query.length < 2 && (
            <div className="py-2">
              {/* Favorites */}
              {favorites.length > 0 && (
                <>
                  <div className="px-4 py-2 text-xs font-medium text-brand-gray uppercase flex items-center gap-2">
                    <Star className="w-3 h-3" />
                    Favoritos
                  </div>
                  {favorites.map((emp, index) => (
                    <div
                      key={`fav-${emp.employeeId}`}
                      className={cn(
                        'px-4 py-2 flex items-center gap-3 cursor-pointer hover:bg-brand-dark-light',
                        selectedIndex === index && 'bg-brand-dark-light'
                      )}
                      onMouseEnter={() => setSelectedIndex(index)}
                      onClick={() => handleSelect(emp)}
                    >
                      <EmployeeAvatar
                        src={emp.pictureUrl ?? undefined}
                        name={emp.name}
                        size="sm"
                      />
                      <span className="text-white flex-1">{emp.name}</span>
                      <Star className="w-4 h-4 text-yellow-400" fill="currentColor" />
                    </div>
                  ))}
                </>
              )}

              {/* Recent */}
              {recentSearches.length > 0 && (
                <>
                  <div className="px-4 py-2 text-xs font-medium text-brand-gray uppercase flex items-center gap-2">
                    <Clock className="w-3 h-3" />
                    Recentes
                  </div>
                  {recentSearches.filter(emp => !favorites.some(f => f.employeeId === emp.employeeId)).map((emp, index) => (
                    <div
                      key={`recent-${emp.employeeId}`}
                      className={cn(
                        'px-4 py-2 flex items-center gap-3 cursor-pointer hover:bg-brand-dark-light',
                        selectedIndex === favorites.length + index && 'bg-brand-dark-light'
                      )}
                      onMouseEnter={() => setSelectedIndex(favorites.length + index)}
                      onClick={() => handleSelect(emp)}
                    >
                      <EmployeeAvatar
                        src={emp.pictureUrl ?? undefined}
                        name={emp.name}
                        size="sm"
                      />
                      <span className="text-white flex-1">{emp.name}</span>
                      <Clock className="w-4 h-4 text-brand-gray" />
                    </div>
                  ))}
                </>
              )}

              {/* Empty state */}
              {favorites.length === 0 && recentSearches.length === 0 && (
                <div className="p-8 text-center text-brand-gray">
                  <p>Digite pelo menos 2 caracteres para buscar</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer hints */}
        <div className="flex items-center justify-between px-4 py-2 border-t border-brand-gray/20 text-xs text-brand-gray">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <ArrowUp className="w-3 h-3" />
              <ArrowDown className="w-3 h-3" />
              Navegar
            </span>
            <span className="flex items-center gap-1">
              <CornerDownLeft className="w-3 h-3" />
              Selecionar
            </span>
          </div>
          <span>ESC para fechar</span>
        </div>
      </div>
    </div>
  )
}
